import Backdrop from '@ui/components/Backdrop/Backdrop';
import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import Card from '@ui/components/Card/Card';
import styles from '@ui/components/Modal/Modal.module.scss';
import { usePreventScroll } from '@ui/hooks';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React from 'react';
import { Dialog, DialogDismiss, DialogHeading } from '@ariakit/react';

const Modal = ({
  store,
  autoFocus = true,
  hideOnEscape = true,
  header,
  children,
  theme = 'default',
  scrollDisabled,
  contentClassName,
  i18n,
  actions,
  size = null,
  preventBodyScroll = false,
  hideOnClickOutside = true,
  tabIndex = -1,
  finalFocusRef,
  className,
  onClose,
  onClickBackdrop = null,
}) => {
  const { animated, mounted, open } = store.useState();
  i18n = i18n || {
    dialog: 'Modal',
  };

  usePreventScroll(mounted && open);

  const handleBackdropClick = e => {
    if (!onClickBackdrop || e.target !== e.currentTarget) {
      return;
    }
    onClickBackdrop(e);
  };

  return (
    <Dialog
      aria-label={i18n.dialog}
      autoFocus={autoFocus}
      backdrop={<Backdrop animated={animated} onClick={handleBackdropClick} />}
      className={classNames(styles.container, {
        [styles.animated]: animated,
      })}
      finalFocus={finalFocusRef}
      hideOnEscape={hideOnEscape}
      hideOnInteractOutside={hideOnClickOutside}
      preventBodyScroll={preventBodyScroll}
      store={store}
      tabIndex={tabIndex}
    >
      <Card
        className={classNames(
          styles.card,
          {
            [styles[size]]: size,
          },
          className,
        )}
      >
        <div
          className={classNames(styles.header, styles[theme], {
            [styles.withClose]: !actions,
          })}
        >
          <DialogHeading render={() => header} />
          {!actions ? (
            <DialogDismiss
              aria-label="Dialog schließen"
              className={styles.close}
              onClick={onClose || store.hide}
              render={
                <FontAwesomeProIcon
                  icon="times"
                  role="presentation"
                  type="solid"
                />
              }
            />
          ) : (
            actions
          )}
        </div>
        <div
          className={classNames(styles.content, contentClassName, {
            [styles.scrollDisabled]: scrollDisabled,
          })}
        >
          {children}
        </div>
      </Card>
    </Dialog>
  );
};

Modal.propTypes = {
  /** Automatically focus the element when it is mounted. */
  autoFocus: PropTypes.bool,
  children: PropTypes.node.isRequired,
  /** Can either be a text or a node */
  header: PropTypes.node.isRequired,
  /** Determines whether the dialog will be hidden when the user clicks or focus on an element outside the dialog. Boolean or callback. */
  hideOnClickOutside: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  /** Determines whether the dialog will be hidden when the user presses the Escape key. */
  hideOnEscape: PropTypes.bool,
  i18n: PropTypes.shape({
    /** `aria-label` of the Dialog */
    dialog: PropTypes.string,
  }),
  onClickBackdrop: PropTypes.func,
  /** Determines whether the body scrolling will be prevented when the dialog is shown. */
  preventBodyScroll: PropTypes.bool,
  size: PropTypes.oneOf(['lg']),
  /* eslint-disable react/forbid-prop-types */
  /** `useDialogStore()` return value from `@ariakit/react` */
  store: PropTypes.object.isRequired,
  tabIndex: PropTypes.number,
  theme: PropTypes.oneOf(['default', 'secondary', 'blue', 'business']),
};

export default Modal;
